import * as React from 'react';
import type { FeatureAppEnvironment, FeatureServices } from '@feature-hub/core';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import type { RenderModeServiceV1 } from '@oneaudi/render-mode-service';
import { ContentContextProvider, FootnoteContextProvider } from '@oneaudi/feature-app-utils';
import type { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import type { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { AudiVideoServiceInterfaceV1 } from '@oneaudi/video-service';
import type { Logger } from '@feature-hub/logger';
import type { OneGraphServiceV1 } from '@oneaudi/onegraph-service';
import type { OneGraphQueryServiceV1 } from '@oneaudi/onegraph-query-service';

import type { AccordionContent } from '../types';
import { AccordionLoader } from './components/Accordion/Accordion';
import ContextProvider from './Context';
import { getDesignLibrary } from './components/utils/alpha-helpers';

interface FeatureServiceDependencies extends FeatureServices {
  readonly 'audi-content-service': ContentServiceV1;
  readonly 'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
  readonly 's2:serialized-state-manager'?: SerializedStateManagerV1;
  readonly 'audi-video-service': AudiVideoServiceInterfaceV1;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'audi-render-mode-service': RenderModeServiceV1;
  readonly 's2:logger'?: Logger;
  readonly 'onegraph-service': OneGraphServiceV1;
  readonly 'onegraph-query-service': OneGraphQueryServiceV1;
}

export default {
  dependencies: {
    externals: {
      react: '*',
      'react-dom': '*',
      'styled-components': '*',
      '@feature-hub/react': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.13.0' }),
    },
    featureServices: {
      's2:logger': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'locale-service': '^1.0.0',
      'onegraph-service': '^1.0.0',
      'onegraph-query-service': '^1.0.0',
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      'audi-video-service': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },
  create: ({
    featureServices: {
      'audi-content-service': contentService,
      'audi-footnote-reference-service': footnoteReferenceServiceScopeManager,
      'locale-service': localeService,
      'audi-render-mode-service': renderModeService,
      's2:async-ssr-manager': asyncSSRManager,
      'audi-video-service': videoService,
      'audi-tracking-service': trackingService,
      'onegraph-service': oneGraphService,
      'onegraph-query-service': oneGraphQueryService,
      's2:logger': loggerService,
    },
    config,
    featureAppId,
    featureAppName,
  }: FeatureAppEnvironment<FeatureServiceDependencies, AccordionContent>) => {
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }
    const { OneGraphProvider } = oneGraphService;
    const requiredServiceForOneGraph = {
      'onegraph-service': oneGraphService,
      'onegraph-query-service': oneGraphQueryService,
      's2:async-ssr-manager': asyncSSRManager,
      'locale-service': localeService,
    };
    return {
      render: () => (
        <ContentContextProvider contentService={contentService}>
          <OneGraphProvider featureServices={requiredServiceForOneGraph}>
            <FootnoteContextProvider
              // eslint-disable-next-line max-len
              footnoteReferenceService={footnoteReferenceServiceScopeManager?.getDefaultScopeRefService()}
            >
              <UniversalEditorProvider
                contentService={contentService}
                renderModeService={renderModeService}
              >
                <ContextProvider
                  featureAppEnv={{ featureAppId, featureAppName }}
                  localeService={localeService}
                  loggerService={loggerService}
                  trackingService={trackingService}
                  videoService={videoService}
                  oneGraphService={oneGraphService}
                >
                  <AccordionLoader initialContent={config} meta={{ featureAppId }} />
                </ContextProvider>
              </UniversalEditorProvider>
            </FootnoteContextProvider>
          </OneGraphProvider>
        </ContentContextProvider>
      ),
    };
  },
};
